import type { AuthState } from './types';

const STATE_TRANSITIONS: Record<AuthState, AuthState[]> = {
  INITIALIZING: ['AUTHENTICATED', 'UNAUTHENTICATED', 'REFRESHING'],
  UNAUTHENTICATED: ['AUTHENTICATING', 'OAUTH_REDIRECT', 'UNAUTHENTICATED'],
  AUTHENTICATING: ['AUTHENTICATED', 'UNAUTHENTICATED'],
  AUTHENTICATED: [
    'REFRESHING',
    'OAUTH_REDIRECT',
    'LOGGED_OUT',
    'UNAUTHENTICATED',
    'AUTHENTICATING',
  ],
  REFRESHING: ['AUTHENTICATED', 'UNAUTHENTICATED'],
  OAUTH_REDIRECT: ['AUTHENTICATED', 'UNAUTHENTICATED'],
  LOGGED_OUT: ['UNAUTHENTICATED', 'AUTHENTICATING'],
};

export class AuthStateMachine {
  private currentState: AuthState = 'INITIALIZING';
  private stateHistory: Array<{ state: AuthState; timestamp: number }> = [];

  constructor(initialState: AuthState = 'INITIALIZING') {
    this.currentState = initialState;
    this.recordStateChange(initialState);
  }

  public getState(): AuthState {
    return this.currentState;
  }

  public setState(state: AuthState): void {
    this.currentState = state;
    this.recordStateChange(state);
  }

  public transition(nextState: AuthState): void {
    // Idempotent transition is a no-op (prevents crash loops on redundant emits).
    if (this.currentState === nextState) {
      console.log(`🔐 AuthStateMachine: ${this.currentState} → ${nextState} (noop)`);
      this.recordStateChange(nextState);
      return;
    }

    if (!this.canTransition(nextState)) {
      const allowed = STATE_TRANSITIONS[this.currentState].join(', ');
      throw new Error(
        `Invalid auth state transition: ${this.currentState} → ${nextState}. ` +
          `Allowed transitions from ${this.currentState}: ${allowed}`
      );
    }

    console.log(`🔐 AuthStateMachine: ${this.currentState} → ${nextState}`);

    this.currentState = nextState;
    this.recordStateChange(nextState);
  }

  public canTransition(nextState: AuthState): boolean {
    const allowedStates = STATE_TRANSITIONS[this.currentState];
    return allowedStates.includes(nextState);
  }

  public getHistory(): Array<{ state: AuthState; timestamp: number }> {
    return [...this.stateHistory];
  }

  private recordStateChange(state: AuthState): void {
    this.stateHistory.push({
      state,
      timestamp: Date.now(),
    });

    if (this.stateHistory.length > 20) {
      this.stateHistory.shift();
    }
  }
}
